import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
    providedIn: 'root',
})
export class LegalDocService {
    readonly LEGAL_B2C_BASE_URL = `${environment.http.legalBaseUrl}apps/${environment.legalAppIdEndCustomer}/documents/`;
    readonly LEGAL_B2B_BASE_URL = `${environment.http.legalBaseUrl}apps/${environment.legalAppIdCustomer}/documents/`;

    constructor(private http: HttpClient) {}

    public validateCountry(countryCode: string, supportedCountries: TranslatedCountry[]): string {
        if (!countryCode) {
            return '';
        }

        const foundCountry = supportedCountries.find(
            (country) => country.countryCode.toUpperCase() === countryCode.toUpperCase(),
        );
        return foundCountry ? foundCountry.countryCode : '';
    }

    public getAllSupportedB2CCountries(): Observable<TranslatedCountry[]> {
        return this.getAllSupportedCountries(this.LEGAL_B2C_BASE_URL);
    }

    public getAllSupportedB2BCountries(): Observable<TranslatedCountry[]> {
        return this.getAllSupportedCountries(this.LEGAL_B2B_BASE_URL);
    }

    public getB2CDocuments(country: string): Observable<Documents> {
        return this.getDocuments(this.LEGAL_B2C_BASE_URL, country);
    }

    public getB2BDocuments(country: string): Observable<Documents> {
        return this.getDocuments(this.LEGAL_B2B_BASE_URL, country);
    }

    private getDocuments(url: string, country: string): Observable<Documents> {
        const params = { country };
        return this.http.get<Documents>(url, { params });
    }

    private getAllSupportedCountries(url: string): Observable<TranslatedCountry[]> {
        return this.http.get<Documents>(url).pipe(
            map(
                (res) =>
                    res.documents
                        .map((document) => document.country)
                        .filter((elem, pos, arr) => !!elem && arr.indexOf(elem) === pos), // remove duplicates
            ),
            switchMap((countries) => {
                return this.initCountries(countries);
            }),
        );
    }

    private initCountries(validCountryList: string[]): Observable<TranslatedCountry[]> {
        const lang = this.getBrowserLang();
        return from(import(`i18n-iso-countries/langs/${lang}.json`)).pipe(
            map((localeData) =>
                validCountryList
                    .map((iso) => ({
                        countryCode: iso,
                        name: localeData.countries[iso.toUpperCase()],
                    }))
                    .filter((c) => !!c.name),
            ),
        );
    }

    private getBrowserLang() {
        return navigator.language.split('-')[0].toLocaleLowerCase();
    }
}

export interface Documents {
    documents: Document[];
    optionalDocuments: Document[];
}

export interface Document {
    href: string;
    revisionId: string;
    title: string;
    language: string;
    country: string;
    notice?: string;
    onlyReading: boolean;
    accepted: boolean;
    link?: string;
}

export interface TranslatedCountry {
    name: string;
    countryCode: string;
}
